import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getGameStats, getSeasonStats, getAllTimeStats } from './store/selectors/teamComparisonSelector';
import initContainerData from 'bdata-react-component/lib/store/initContainerData';
import teamStatsGameReducer from './store/reducers/teamStatsGame';
import teamStatsSeasonAllTimeReducer from './store/reducers/teamStatsSeasonAllTime';
import TeamStats from './TeamStats';
import { Alignments, TableDataTypes } from '@mlb/react-patterns/components/Table/table.types';
import { useTranslator } from '@gameday/utils/hooks/useTranslator';
import { BoldCell, TeamNameWrapper } from './TeamStats.style';
import numberFormatters from '@gameday/utils/numberFormatters';
import { TeamLogoTypes } from '@mlb/react-patterns/components/TeamLogo/teamLogo.types';
import TeamLogo from '@mlb/react-patterns/components/TeamLogo/teamLogo';

const { SPOT } = TeamLogoTypes;

initContainerData.register({
    reducers: {
        teamStatsGame: teamStatsGameReducer,
        teamStatsSeasonAllTime: teamStatsSeasonAllTimeReducer
    }
});

const selectorsMap = {
    game: getGameStats,
    season: getSeasonStats,
    alltime: getAllTimeStats
};

const TeamCell = ({
    data
}) => {
    const {
        team,
        name
    } = data;

    return (
        <div>
            <TeamLogo
                teamId={team}
                width={32}
                height={32}
                teamLogoType={ SPOT }
                alt={ name }
            />
            <TeamNameWrapper>{ name }</TeamNameWrapper>
        </div>

    );
};

const formatScore = (type, score, language) => {
    if (typeof score === 'undefined') {
        return '-';
    }

    if (type === 'wins' || type === 'runs') {
        return numberFormatters[language].format(score);
    } else if (type === 'era') {
        return (score * 1)?.toFixed(2);
    } else if (type === 'avg') {
        return score?.replace(/^0+(?!\.|$)/, '');
    } else {
        return score;
    }
};

const SmartTDCell = ({
    columnData,
    columnIndex,
    data
}) => {
    const { otherteam = {}, language, scoreDisplayLogic } = data;
    const thisCol = columnData[columnIndex].dataField;
    const myScore = data[thisCol];
    const theirScore = otherteam[thisCol];
    const formattedScore = formatScore(thisCol, myScore, language);
    let isBold = false;

    if (scoreDisplayLogic[thisCol] === 'greater') {
        isBold = myScore > theirScore;
    } else if (scoreDisplayLogic[thisCol] === 'lower') {
        isBold = myScore < theirScore;
    }

    return (
        (isBold) ? <BoldCell>{formattedScore}</BoldCell> : <span>{formattedScore}</span>
    );
};

const customCells = {
    game: [
        {
            cellType: 'body',
            customCell: <TeamCell />,
            dataField: 'team'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'runs'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'hits'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'hr'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'totalBases'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'stolenBases'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'leftOnBase'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'hr'
        }

    ],
    season: [
        {
            cellType: 'body',
            customCell: <TeamCell />,
            dataField: 'team'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'wins'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'runs'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'hr'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'avg'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'era'
        }
    ],
    alltime: [
        {
            cellType: 'body',
            customCell: <TeamCell />,
            dataField: 'team'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'wins'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'runs'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'hr'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'avg'
        },
        {
            cellType: 'body',
            customCell: <SmartTDCell />,
            dataField: 'era'
        }
    ]
};

const TeamStatsDataContainer = ({
    type,
    mobileWidth
}) => {
    const { tableRows, gameDate, season, teamComparisonGameType, gameType = '' } = useSelector(selectorsMap[type]);
    const gameTableHead = [
        {
            content: '',
            dataField: 'team',
            sortField: 'team',
            sortable: false,
            alignment: Alignments.LEFT,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_h_r', 'R'),
            dataField: 'runs',
            sortField: 'runs',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_h_h', 'H'),
            dataField: 'hits',
            sortField: 'hits',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_h_hr', 'HR'),
            dataField: 'hr',
            sortField: 'homeRuns',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_tb', 'TB'),
            dataField: 'totalBases',
            sortField: 'totalBases',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_h_sb', 'SB'),
            dataField: 'stolenBases',
            sortField: 'stolenBases',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_lob', 'LOB'),
            dataField: 'leftOnBase',
            sortField: 'leftOnBase',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_e', 'E'),
            dataField: 'errors',
            sortField: 'errors',
            sortable: false,
            alignment: Alignments.RIGHT,
            type: TableDataTypes.NUMBER
        }
    ];

    const othersTableHead = [
        {
            content: '',
            dataField: 'team',
            alignment: Alignments.LEFT,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_w', 'W'),
            dataField: 'wins',
            sortField: 'wins',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_h_r', 'R'),
            dataField: 'runs',
            sortField: 'runs',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_h_hr"', 'HR'),
            dataField: 'hr',
            sortField: 'hr',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_h_avg"', 'AVG'),
            dataField: 'avg',
            sortField: 'avg',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        },
        {
            content: useTranslator('labelBrief_era"', 'ERA'),
            dataField: 'era',
            sortField: 'era',
            sortable: false,
            alignment: Alignments.CENTER,
            type: TableDataTypes.NUMBER
        }
    ];

    const tableHeadMap = {
        game: gameTableHead,
        season: othersTableHead,
        alltime: othersTableHead
    };

    const tableHead = tableHeadMap[type];
    let footer;

    switch (type) {
        case 'season':
            if (teamComparisonGameType !== 'P') {
                footer = useTranslator('label_season_footer', `${season} season through ${gameDate}`, {
                    season,
                    game_date: gameDate
                });
            } else {
                const roundKey = `gameTypes_${gameType}`;
                const translatedRound = useTranslator(roundKey, '');

                footer = useTranslator('label_series_footer', `${season} ${translatedRound} through ${gameDate}`, {
                    season,
                    game_date: gameDate,
                    round: translatedRound
                });
            }

            break;
        case 'alltime':
            if (teamComparisonGameType !== 'P') {
                footer = useTranslator('label_alltime_footer', `1974 through ${gameDate}`, {
                    game_date: gameDate
                });
            } else {
                footer = useTranslator('label_alltime_postseason_footer', `1974 postseason through ${gameDate}`, {
                    game_date: gameDate,
                    season
                });
            }
            break;
        default:
            footer = useTranslator('label_default_footer', '');
    }

    return (
        <TeamStats
            customCells={customCells[type]}
            thead={tableHead}
            tbody={tableRows}
            type={type}
            footer={footer}
            mobileWidth={mobileWidth}
        />
    );
};

TeamCell.propTypes = {
    data: PropTypes.object
};

SmartTDCell.propTypes = {
    columnData: PropTypes.array,
    columnIndex: PropTypes.number,
    data: PropTypes.object
};

TeamStatsDataContainer.propTypes = {
    type: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf(['game', 'season', 'alltime'])
    ]),
    mobileWidth: PropTypes.bool
};

export default TeamStatsDataContainer;
